import { useRef, useEffect, useCallback } from "react";

export default function ScrollToTopButton() {
  const containerRef = useRef<HTMLButtonElement>(null);
  const circleRef = useRef<SVGCircleElement>(null);
  const rafRef = useRef<number>(0);
  const ticking = useRef(false);

  const size = 44;
  const stroke = 2;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;

  const updateUI = useCallback((scrollY: number, docH: number) => {
    const progress = docH > 0 ? Math.min(100, (scrollY / docH) * 100) : 0;
    const visible = scrollY > 500;

    // Update SVG ring directly — zero React re-renders during scroll
    if (circleRef.current) {
      const dash = circ - (progress / 100) * circ;
      circleRef.current.style.strokeDashoffset = String(dash);
    }

    // Toggle visibility class directly on the button
    const btn = containerRef.current;
    if (btn) {
      const base = "fixed bottom-6 right-6 z-50 min-w-[44px] min-h-[44px] w-11 h-11 flex items-center justify-center rounded-full cursor-pointer transition-all duration-300 ";
      if (visible) {
        btn.className = base + "opacity-100 translate-y-0 pointer-events-auto";
      } else {
        btn.className = base + "opacity-0 translate-y-3 pointer-events-none";
      }
    }
  }, [circ]);

  useEffect(() => {
    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      rafRef.current = requestAnimationFrame(() => {
        ticking.current = false;
        const scrollY = window.scrollY;
        const docH = document.documentElement.scrollHeight - window.innerHeight;
        updateUI(scrollY, docH);
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    // Initial update so button is hidden on mount without a flash
    updateUI(window.scrollY, document.documentElement.scrollHeight - window.innerHeight);

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [updateUI]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <button
      ref={containerRef}
      onClick={scrollToTop}
      aria-label="Scroll to top"
      className="fixed bottom-6 right-6 z-50 min-w-[44px] min-h-[44px] w-11 h-11 flex items-center justify-center rounded-full cursor-pointer transition-all duration-300 opacity-0 translate-y-3 pointer-events-none"
      style={{ background: "#0B1628", border: "1px solid rgba(255,255,255,0.08)" }}
    >
      {/* Progress ring — updated directly via ref, no React state */}
      <svg
        width={size}
        height={size}
        className="absolute inset-0"
        style={{ transform: "rotate(-90deg)" }}
        aria-hidden="true"
      >
        {/* Track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={stroke}
        />
        {/* Fill */}
        <circle
          ref={circleRef}
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#F47C20"
          strokeWidth={stroke}
          strokeDasharray={circ}
          strokeDashoffset={circ}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.15s ease" }}
        />
      </svg>
      {/* Arrow icon */}
      <i className="ri-arrow-up-line text-sm text-white relative z-10" />
    </button>
  );
}
